/**
 * Channel-connection metadata used by Step 3's card grid per ADR 0016.
 *
 * Channels are eCommerce platforms (BigCommerce, future Shopify,
 * Amazon, etc.) that source orders into Mailchute. Carriers (per ADR
 * 0015 and `carrier-connections.ts`) are the parallel concept on the
 * outbound side — different table, different SDK packages, same
 * visual treatment in the onboarding wizard.
 *
 * This module is the public-shape catalogue only — runtime calls to
 * mutate connection state live in `connections.ts` (BC-specific
 * claim, per ADR 0011) and per-platform handlers landing in future
 * PRs. There is no `POST /channel-connections` analogue at MVP
 * because every platform's connection flow is bespoke (BC-initiated
 * OAuth, Shopify-initiated OAuth, custom-endpoint paste, …).
 */

/**
 * eCommerce platforms Mailchute will integrate with. The DB enum
 * (`connections.kind`) is the minimal "shippable today" subset
 * (`bigcommerce` only at MVP); everything else here is UI-only — it
 * gates the Step 3 card grid's "Coming soon" tiles per ADR 0016.
 *
 * Append-only: never reuse a value. Adding a new entry here renders
 * a card; making it interactive requires its own SDK package +
 * Connect dialog + DB enum entry in a follow-up PR.
 */
export const CHANNEL_KINDS = [
  'bigcommerce',
  'shopify',
  'magento',
  'woocommerce',
  'amazon',
  'ebay',
  'etsy',
  'notonthehighstreet',
  'squarespace',
  'wix',
  'tiktokshop',
  'custom',
] as const;
export type ChannelKind = (typeof CHANNEL_KINDS)[number];

/**
 * The subset of `CHANNEL_KINDS` that the backend can actually drive
 * today. Used to type-narrow Connect actions and to gate the Connect
 * button's enabled / disabled state in the card grid.
 */
export const CONNECTABLE_CHANNEL_KINDS = ['bigcommerce'] as const;
export type ConnectableChannelKind = (typeof CONNECTABLE_CHANNEL_KINDS)[number];

export function isConnectableChannelKind(kind: ChannelKind): kind is ConnectableChannelKind {
  return (CONNECTABLE_CHANNEL_KINDS as readonly string[]).includes(kind);
}
